import { useState } from 'react';
import { Table, type Column } from '@/shared/ui/Table';
import { Modal } from '@/shared/ui/Modal';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { SkeletonCard } from '@/shared/ui/Skeleton';
import { useRankTable } from '../hooks/useRankTable';
import { AiAnswersSummary, TermAiAnswers } from './AiAnswers';
import { RankTrendChart, type TrendSeries } from './RankTrendChart';
import type { LocalRankCell, RankCell, RankCompetitor, RankTableRow } from '../api/rankingsApi';
import styles from './RankingsSection.module.css';

interface Row extends RankTableRow {
  id: string;
}

/** How a surface's cell reads: its position, or why there is none. */
function Cell({
  cell,
  depth,
  ai,
}: {
  cell: RankCell | LocalRankCell | null;
  depth: number;
  ai?: boolean;
}) {
  if (!cell) return <span className={styles.muted}>Not checked</span>;
  if (cell.rank === null) {
    return <span className={styles.muted}>{ai ? 'Not mentioned' : `Not in the top ${depth}`}</span>;
  }
  // Rank 1 is first, so a lower number than last week is a move up. A page that was not found last
  // week has no number to compare with, so it shows no arrow.
  const before = cell.previous?.rank ?? null;
  const moved = before === null ? 0 : before - cell.rank;
  return (
    <span>
      <span className={styles.rank}>#{cell.rank}</span>
      {moved > 0 && <span className={styles.up}>▲{moved}</span>}
      {moved < 0 && <span className={styles.down}>▼{-moved}</span>}
      {'packRank' in cell && cell.packRank !== null && (
        <Badge variant="success" size="sm" className={styles.packBadge}>
          In map pack
        </Badge>
      )}
    </span>
  );
}

const SURFACES = [
  { key: 'google_organic', label: 'Google' },
  { key: 'google_local_finder', label: 'Local results' },
  { key: 'chatgpt', label: 'ChatGPT' },
] as const;

function businessLabel(business: RankCompetitor): string {
  return business.name ?? business.domain ?? 'Unnamed business';
}

function weekLabel(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function rankText(rank: number | null | undefined, depth: number): string {
  if (rank === undefined) return '—';
  return rank === null ? `Not in the top ${depth}` : `#${rank}`;
}

interface TrendRow {
  id: string;
  weekOf: string;
}

/** The side panel for one term: what was searched, who else shows up, and how it has moved. */
function TermPanel({ row, depth }: { row: RankTableRow; depth: number }) {
  const [showTable, setShowTable] = useState(false);
  const googleQuery = row.google_local_finder?.queries[0] ?? row.google_organic?.queries[0] ?? null;
  const withBusinesses = SURFACES.flatMap(({ key, label }) => {
    const cell = row[key];
    return cell && cell.competitors.length > 0 ? [{ key, label, cell }] : [];
  });

  const chartSeries: TrendSeries[] = SURFACES.flatMap(({ key, label }) => {
    const trend = row[key]?.trend ?? [];
    return trend.length > 0 ? [{ key, label, points: trend }] : [];
  });

  const weeks = [
    ...new Set(SURFACES.flatMap(({ key }) => row[key]?.trend.map((t) => t.weekOf) ?? [])),
  ]
    .sort()
    .reverse();
  const trendRows: TrendRow[] = weeks.map((weekOf) => ({ id: weekOf, weekOf }));
  const trendColumns: Column<TrendRow>[] = [
    { key: 'week', header: 'Week of', render: (r) => weekLabel(r.weekOf) },
    ...SURFACES.map(
      ({ key, label }): Column<TrendRow> => ({
        key,
        header: label,
        render: (r) => rankText(row[key]?.trend.find((t) => t.weekOf === r.weekOf)?.rank, depth),
      }),
    ),
  ];

  return (
    <>
      <section className={styles.panelSection}>
        <h3 className={styles.panelTitle}>What we searched</h3>
        {googleQuery ? (
          <p className={styles.query}>
            Google: <strong>&ldquo;{googleQuery}&rdquo;</strong>
          </p>
        ) : (
          <p className={styles.muted}>Not checked on Google yet.</p>
        )}
        {googleQuery && (
          <p className={styles.note}>
            From a phone at your practice&apos;s address, so it matches what a patient nearby sees.
          </p>
        )}
      </section>

      <TermAiAnswers term={row.term} />

      <section className={styles.panelSection}>
        <h3 className={styles.panelTitle}>Top businesses besides you</h3>
        {withBusinesses.length === 0 && (
          <p className={styles.muted}>No other businesses were recorded for this term yet.</p>
        )}
        {withBusinesses.map(({ key, label, cell }) => (
          <div key={key} className={styles.aheadGroup}>
            <h4 className={styles.aheadTitle}>
              {label} · you are {rankText(cell.rank, depth).toLowerCase()}
            </h4>
            <ul className={styles.aheadList}>
              {cell.competitors.map((business) => (
                <li key={`${business.position}-${businessLabel(business)}`}>
                  #{business.position} {businessLabel(business)}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </section>

      <section className={styles.panelSection}>
        <div className={styles.panelHeader}>
          <h3 className={styles.panelTitle}>Week over week</h3>
          {chartSeries.length > 0 && (
            <Button variant="ghost" size="sm" onClick={() => setShowTable((value) => !value)}>
              {showTable ? 'View as chart' : 'View as table'}
            </Button>
          )}
        </div>
        {chartSeries.length === 0 && <p className={styles.muted}>No history yet.</p>}
        {chartSeries.length > 0 && showTable && (
          <Table columns={trendColumns} data={trendRows} emptyMessage="No history yet." />
        )}
        {chartSeries.length > 0 && !showTable && (
          <RankTrendChart series={chartSeries} depth={depth} />
        )}
      </section>
    </>
  );
}

/**
 * Where the practice ranks for each of its search terms in its city: Google web results, Google's
 * local results (the map pack and the list after it) and ChatGPT. The same table Sam reports each
 * week. Clicking a term opens a side panel with the exact search, how ChatGPT answered each patient
 * question, the top businesses and the trend. Under the table, how often ChatGPT recommends the practice.
 */
export function RankingsSection({ locationId }: { locationId: string }) {
  const { data, isLoading, isError } = useRankTable(locationId);
  const [openTerm, setOpenTerm] = useState<string | null>(null);

  if (isLoading) return <SkeletonCard />;
  if (isError || !data) {
    return (
      <section aria-label="Search rankings">
        <h2 className={styles.title}>Search rankings</h2>
        <p className={styles.description}>Rankings could not be loaded. Try again shortly.</p>
      </section>
    );
  }

  const rows: Row[] = data.rows.map((row) => ({ ...row, id: row.term }));
  const columns: Column<Row>[] = [
    { key: 'term', header: 'Search term' },
    {
      key: 'google_organic',
      header: 'Google',
      render: (row) => <Cell cell={row.google_organic} depth={data.depth} />,
    },
    {
      key: 'google_local_finder',
      header: 'Local results',
      render: (row) => <Cell cell={row.google_local_finder} depth={data.depth} />,
    },
    {
      key: 'chatgpt',
      header: 'ChatGPT',
      render: (row) => <Cell cell={row.chatgpt} depth={data.depth} ai />,
    },
  ];

  const openRow = rows.find((row) => row.term === openTerm);

  const checkedOn =
    data.observedAt === null ? null : new Date(data.observedAt).toLocaleDateString();

  return (
    <section aria-label="Search rankings">
      <h2 className={styles.title}>Search rankings</h2>
      <p className={styles.description}>
        Where you show up when a patient searches each term in your city. Sam checks every week.
      </p>
      <Table
        columns={columns}
        data={rows}
        onRowClick={(row) => setOpenTerm(row.term)}
        emptyMessage="Save search terms above, and your first check appears after Sam's next weekly run."
      />
      <Modal
        isOpen={openTerm !== null}
        onClose={() => setOpenTerm(null)}
        title={openTerm ?? undefined}
        variant="side"
      >
        {openRow && <TermPanel row={openRow} depth={data.depth} />}
      </Modal>
      {checkedOn && (
        <p className={styles.note}>
          Checked {checkedOn} from your practice&apos;s location. Position 1 is first; click a term
          to see who shows up for it. Patients elsewhere in the city can see a different order.
        </p>
      )}
      <AiAnswersSummary />
    </section>
  );
}
